import React, { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { toDateTimeLocalValue } from '../citizen/serviceprovider/medicine/medicine-date-time';

interface UseOfForceMunicipalitySendingDialogProps {
  municipalityName?: string;
  title?: string;
  description?: string;
  fieldLabel?: string;
  inputType?: 'datetime-local' | 'date';
  onSave: (sentAt: Date) => Promise<void>;
  onClose: () => void;
}

export function UseOfForceMunicipalitySendingDialog(props: UseOfForceMunicipalitySendingDialogProps) {
  const {
    municipalityName,
    title = 'Registrér afsendelse til handlekommunen',
    description = `Borgerens handlekommune er ${municipalityName ?? 'en anden kommune'}. Hent skemaet som PDF, send det til handlekommunen, og registrér her, hvornår det blev sendt. Registreringen kan ikke ændres bagefter.`,
    fieldLabel = 'Sendt til handlekommunen (dato og kl.)',
    inputType = 'datetime-local',
    onSave,
    onClose,
  } = props;
  const isDateOnly = inputType === 'date';
  const [sentAt, setSentAt] = useState(() => toDateTimeLocalValue(new Date()).slice(0, isDateOnly ? 10 : undefined));
  const [isSaving, setIsSaving] = useState(false);

  async function save() {
    setIsSaving(true);
    try {
      // A plain date is read as local midnight, so the day does not shift with the time zone.
      await onSave(new Date(isDateOnly ? `${sentAt}T00:00` : sentAt));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onClose={() => (isSaving ? undefined : onClose())} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2">{description}</Typography>
          <TextField
            label={fieldLabel}
            type={inputType}
            value={sentAt}
            onChange={event => setSentAt(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={isSaving}>
          Afbryd
        </Button>
        <Button variant="contained" onClick={save} disabled={isSaving || sentAt === ''}>
          Registrér afsendelse
        </Button>
      </DialogActions>
    </Dialog>
  );
}
